var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useRef, useEffect, useState, } from "react";
import { VALIDATION_STATE } from "../../hooks/useValidation";
import { Spinner } from "../spinner/Spinner";
import cx from "classnames";
import { FormContext } from "./FormContext";
import "./Form.scss";
import { scrollToError } from "./FormErrors";
export class LysaFormRef {
    constructor(setForceErrors) {
        this.setForceErrors = setForceErrors;
        this.inputs = [];
        this.onChangeListeners = [];
        this.addOnInputUpdate = (fn) => {
            fn(this.inputs);
            this.onChangeListeners.push(fn);
            return () => {
                const index = this.onChangeListeners.indexOf(fn);
                if (index >= 0) {
                    this.onChangeListeners.splice(index, 1);
                }
            };
        };
    }
    updateInput(newInput) {
        const existingIdx = this.inputs.findIndex((input) => input.id === newInput.id);
        if (existingIdx > -1) {
            this.inputs.splice(existingIdx, 1, newInput);
        }
        else {
            this.inputs.push(newInput);
        }
        this.callOnChangeListeners();
    }
    removeInput(inputId) {
        const existingIdx = this.inputs.findIndex((input) => input.id === inputId);
        if (existingIdx > -1) {
            this.inputs.splice(existingIdx, 1);
            this.callOnChangeListeners();
        }
    }
    callOnChangeListeners() {
        this.onChangeListeners.map((listener) => listener([...this.inputs]));
    }
    resetValidation() {
        this.inputs.forEach((input) => input.resetValidation());
    }
    isInputValid(id) {
        var _a;
        return ((_a = this.inputs.find((input) => input.id === id)) === null || _a === void 0 ? void 0 : _a.isValid) === true;
    }
    get isValid() {
        return this.inputs.every((input) => input.isValid);
    }
    get isInvalid() {
        return !this.inputs.every((input) => input.isValid);
    }
    get isPending() {
        return this.inputs.some((input) => input.validationState === VALIDATION_STATE.PENDING);
    }
}
export const Form = (_a) => {
    var { lysaFormRef, onSubmit, forceValidation } = _a, props = __rest(_a, ["lysaFormRef", "onSubmit", "forceValidation"]);
    const [forceErrors, setForceErrors] = useState(forceValidation);
    const formRef = useRef(new LysaFormRef((value) => setForceErrors(value)));
    const [showSpinner, setShowSpinner] = useState(false);
    const delayedSubmit = useRef();
    const formProps = Object.assign({}, props);
    const onSubmitDelayed = useCallback((event) => {
        event.preventDefault();
        event.persist();
        if (!forceErrors) {
            setForceErrors(true);
        }
        if (formRef.current.isPending && onSubmit) {
            setShowSpinner(true);
            delayedSubmit.current = () => onSubmit(event);
        }
        else {
            onSubmit && onSubmit(event);
            if (formRef.current.isValid) {
                setForceErrors(false);
                formRef.current.resetValidation();
            }
            else {
                const firstInputError = formRef.current.inputs.find((input) => input.isInvalid);
                if (firstInputError) {
                    scrollToError(firstInputError.id, firstInputError.scrollToRef);
                }
            }
        }
    }, [onSubmit, forceErrors]);
    const updateInput = useCallback((newInput) => {
        formRef.current.updateInput(newInput);
        if (!formRef.current.isPending) {
            setShowSpinner(false);
            setTimeout(() => {
                var _a;
                if (formRef.current.isPending) {
                    return;
                }
                if (delayedSubmit.current) {
                    (_a = delayedSubmit.current) === null || _a === void 0 ? void 0 : _a.call(delayedSubmit);
                    delayedSubmit.current = undefined;
                    if (formRef.current.isValid) {
                        setForceErrors(false);
                        formRef.current.resetValidation();
                    }
                }
            });
        }
    }, []);
    const removeInput = useCallback((inputId) => {
        formRef.current.removeInput(inputId);
    }, []);
    useEffect(() => {
        if (lysaFormRef) {
            lysaFormRef.current = formRef.current;
        }
    }, [lysaFormRef]);
    useEffect(() => {
        setForceErrors(forceValidation);
    }, [forceValidation]);
    return (React.createElement("form", Object.assign({}, formProps, { className: cx(formProps.className, "lysa-ui-form", {
            "lysa-ui-form-pending": showSpinner,
        }), onSubmit: onSubmitDelayed, noValidate: true }),
        React.createElement(FormContext.Provider, { value: {
                forceErrors: forceErrors || false,
                updateInput,
                removeInput,
            } },
            props.children,
            showSpinner ? (React.createElement("div", { className: "form-pending-spinner" },
                React.createElement(Spinner, null))) : null)));
};
